import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const ACTIVITIES = [
  {
    to: '/activities/compass-read',
    icon: '🧭',
    name: 'Compass Read',
    type: 'Timing · Precision',
    time: '2–5 min',
    difficulty: 'Progressive',
    desc: 'A compass needle sweeps across cardinal directions. Tap or press Space to stop it precisely in the target sector. The target narrows as rounds progress.',
    mechanic: 'A sweeping needle oscillates between compass points. You stop it at the right moment.',
    points: 'Up to 120 per round',
    color: '#c9a84c',
  },
  {
    to: '/activities/route-builder',
    icon: '🗺️',
    name: 'Route Builder',
    type: 'Puzzle · Pathfinding',
    time: '3–8 min',
    difficulty: 'Consistent',
    desc: 'Navigate a hex-terrain grid from the start point to the destination in as few moves as possible. Obstacles are randomized each round.',
    mechanic: 'Click hexagons to trace a path. Fewer moves = higher session score.',
    points: 'Up to 200 per route',
    color: '#a8cfe0',
  },
  {
    to: '/activities/altitude-gauge',
    icon: '⛰️',
    name: 'Altitude Gauge',
    type: 'Timing · Observation',
    time: '2–4 min',
    difficulty: 'Escalating',
    desc: 'A vertical altitude marker oscillates up and down a scale. Press Mark — or Space — when the indicator enters the target elevation zone.',
    mechanic: 'A vertical marker moves at increasing speeds. Stop it inside the coloured zone.',
    points: 'Up to 150 per reading',
    color: '#7fa8c0',
  },
];

export default function Activities() {
  return (
    <div style={{ minHeight: '100vh', padding: '60px 0 100px' }}>
      <div className="container">

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ marginBottom: '56px' }}>
          <div className="label label-sky" style={{ marginBottom: '14px' }}>Three Activities</div>
          <h1 style={{ color: '#fff', marginBottom: '16px' }}>Navigation Activities</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', marginBottom: '16px', fontSize: '1.2rem' }}>Choose your next route challenge</h2>
          <div className="divider" />
          <p style={{ maxWidth: '520px', marginTop: '16px' }}>
            Each activity uses a distinct interaction model. None share mechanics with any other activity here or on any other platform. Session points only — no monetary value, no purchases, no prizes.
          </p>
        </motion.div>

        {/* Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          style={{
            background: 'rgba(168,207,224,0.04)', border: '1px solid rgba(168,207,224,0.1)',
            borderRadius: '12px', padding: '14px 20px', marginBottom: '40px',
            fontSize: '0.82rem', color: 'rgba(168,207,224,0.5)',
          }}
        >
          All activities are browser-based, free to use, and require no account. Session points reset when you close your browser and have no monetary value.
        </motion.div>

        {/* Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {ACTIVITIES.map(({ to, icon, name, type, time, difficulty, desc, mechanic, points, color }, i) => (
            <motion.div key={to}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.12, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link to={to} style={{ textDecoration: 'none' }}>
                <div className="card" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: '2.8rem', flexShrink: 0 }}>{icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>
                        <h3 style={{ color: '#fff', fontSize: '1.5rem' }}>{name}</h3>
                        <div className="label label-amber">{type}</div>
                      </div>
                      <p style={{ marginBottom: '14px', fontSize: '0.92rem' }}>{desc}</p>
                      <div style={{ fontSize: '0.82rem', color: 'rgba(168,207,224,0.5)', lineHeight: 1.7 }}>
                        <strong style={{ color: 'rgba(168,207,224,0.7)' }}>Mechanic:</strong> {mechanic}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '140px' }}>
                      {[
                        { label: 'Session', val: time },
                        { label: 'Difficulty', val: difficulty },
                        { label: 'Points', val: points },
                      ].map(({ label, val }) => (
                        <div key={label} style={{ background: 'rgba(168,207,224,0.04)', borderRadius: '8px', padding: '8px 12px' }}>
                          <div style={{ fontSize: '0.65rem', color: 'rgba(168,207,224,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
                          <div style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500, marginTop: '2px' }}>{val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'Syne, sans-serif', fontSize: '0.85rem', fontWeight: 600, color }}>
                      Open activity
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M3 8H13M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Bottom disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          style={{
            marginTop: '48px', padding: '20px 24px',
            background: 'rgba(201,168,76,0.05)',
            border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: '14px', fontSize: '0.8rem',
            color: 'rgba(168,207,224,0.45)', lineHeight: 1.8,
          }}
        >
          <strong style={{ color: 'rgba(201,168,76,0.7)', display: 'block', marginBottom: '4px' }}>Activity Notice</strong>
          All navigation activities on Social North Hub use session points only. Session points are not currency and have zero monetary value. They cannot be purchased, transferred, redeemed, or exchanged for any real-world benefit. This platform does not accept deposits and does not award cash prizes. Available to adults 18 and over in Canada. Free entertainment only.
        </motion.div>
      </div>
    </div>
  );
}
